"use client";

import { useState } from "react";
import { Image as ImageIcon, X } from "lucide-react";
import { useAuthorStore } from "@/stores/authorStore";
import { showToast } from "@/lib/toast";

interface ModalCreateAuthorProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
}

export default function ModalCreateAuthor({ isOpen, onClose, onComplete }: ModalCreateAuthorProps) {
  const { createAuthor, isCreating } = useAuthorStore();
  const [form, setForm] = useState({ name: "", info: "" });
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [errors, setErrors] = useState<{ name?: string }>({});

  const validate = () => {
    const e: { name?: string } = {};
    if (!form.name.trim()) e.name = "Tên tác giả không được để trống";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!validate()) return;
    let payload: FormData | { name: string; info?: string };
    if (file) {
      const fd = new FormData();
      fd.append('name', form.name);
      if (form.info) fd.append('info', form.info);
      fd.append('image', file);
      payload = fd;
    } else {
      payload = { name: form.name, ...(form.info ? { info: form.info } : {}) };
    }

    const ok = await createAuthor(payload as any);
    if (ok) {
      showToast.success("Thành công", "Tạo tác giả thành công");
      onComplete();
      onClose();
    } else {
      showToast.error("Lỗi", "Không thể tạo tác giả");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-lg mx-4">
        <form onSubmit={submit}>
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">Thêm tác giả</h2>
            <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-700">×</button>
          </div>

          <div className="px-6 py-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tên tác giả</label>
              <input
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.name ? "border-red-500" : "border-gray-300"}`}
              />
              {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Hình ảnh</label>
              <div
                className={`rounded-lg ${preview ? 'p-3' : 'p-8'} ${preview ? 'inline-flex' : 'block'} items-center justify-center text-center cursor-pointer transition-colors ${
                  isDragging
                    ? "border-2 border-dashed border-black bg-white"
                    : "border border-dashed border-gray-300 hover:bg-gray-50"
                }`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsDragging(false);
                  const f = e.dataTransfer.files?.[0];
                  if (f) {
                    setFile(f);
                    setPreview(URL.createObjectURL(f));
                  }
                }}
                onClick={() => (document.getElementById('author-image-input') as HTMLInputElement)?.click()}
              >
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const f = e.target.files?.[0] || null;
                    setFile(f);
                    setPreview(f ? URL.createObjectURL(f) : null);
                  }}
                  className="hidden"
                  id="author-image-input"
                />
                {!preview && (
                  <div className="flex flex-col items-center justify-center gap-2 text-gray-500">
                    <ImageIcon className="h-8 w-8" />
                    <div className="text-sm">Kéo & thả hoặc bấm để thêm ảnh poster</div>
                    <div className="text-xs text-gray-400">(Chỉ nhận file ảnh, tối đa 5MB)</div>
                  </div>
                )}
                {preview && (
                  <div className="relative inline-block">
                    <img src={preview} alt="preview" className="h-48 w-auto rounded-xl border object-contain" />
                    <button
                      type="button"
                      aria-label="remove"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFile(null);
                        setPreview(null);
                      }}
                      className="absolute top-2 right-2 h-7 w-7 rounded-full bg-white text-gray-700 shadow flex items-center justify-center hover:bg-gray-100 border"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Thông tin</label>
              <textarea
                value={form.info}
                onChange={(e) => setForm((p) => ({ ...p, info: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 border-gray-300 min-h-[90px]"
              />
            </div>
          </div>

          <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
            <button type="submit" disabled={isCreating} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {isCreating ? "Đang tạo..." : "Tạo tác giả"}
            </button>
            <button type="button" onClick={onClose} disabled={isCreating} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">Hủy</button>
          </div>
        </form>
      </div>
    </div>
  );
}


